import React, { useState } from 'react';
import { saveFormProgress, loadFormProgressAsync, SavedFormState } from './data/resumeStore';

interface SaveResumeModalProps {
  mode: 'SAVE' | 'RESUME';
  destination: string;
  step?: number;
  formValues?: Record<string, any>;
  applicants?: any[];
  photoBase64?: string;
  passportBase64?: string;
  onClose: () => void;
  onRestore: (saved: SavedFormState) => void;
}

export const SaveResumeModal: React.FC<SaveResumeModalProps> = ({
  mode,
  destination,
  step = 1,
  formValues = {},
  applicants,
  photoBase64,
  passportBase64,
  onClose,
  onRestore,
}) => {
  const [savedResult, setSavedResult] = useState<SavedFormState | null>(null);
  const [resumeInputCode, setResumeInputCode] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const handleSaveClick = () => {
    const res = saveFormProgress(destination, step, formValues, applicants, photoBase64, passportBase64);
    setSavedResult(res);
  };

  const handleResumeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = resumeInputCode.trim();
    if (!trimmed) {
      setErrorMsg('Please enter a recovery code (e.g. MYS-8391).');
      return;
    }

    setIsSearching(true);
    setErrorMsg('');

    try {
      const restored = await loadFormProgressAsync(trimmed);
      if (restored) {
        setErrorMsg('');
        onRestore(restored);
        onClose();
        alert(`✅ Restored application progress for ${restored.applicantName || 'Applicant'} (Step ${restored.step})!`);
      } else {
        setErrorMsg('❌ Recovery code not found. Please verify the code.');
      }
    } catch (err) {
      setErrorMsg('❌ Error restoring code. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleCopyLink = () => {
    if (!savedResult) return;
    const destPath = (savedResult.destination || destination || 'india').toLowerCase();
    const route = destPath.includes('malaysia') ? 'malaysia' : destPath.includes('vietnam') ? 'vietnam' : 'india';
    const url = `${window.location.origin}${window.location.pathname}#/${route}?resume=${savedResult.code}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(5px)',
        zIndex: 99999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '1.75rem',
          maxWidth: '460px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            {mode === 'SAVE' ? '💾 Save & Resume Later' : '🔑 Resume Saved Application'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: '1.25rem', cursor: 'pointer', color: '#64748b' }}
          >
            ✕
          </button>
        </div>

        {mode === 'SAVE' ? (
          <div>
            {!savedResult ? (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1.25rem' }}>
                  Click below to generate a 6-character recovery code. You can pause filling out your {destination} e-Visa application and resume anytime on any device!
                </p>
                <button
                  type="button"
                  onClick={handleSaveClick}
                  className="btn-primary"
                  style={{ width: '100%', padding: '0.85rem', fontSize: '1rem', background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}
                >
                  ⚡ Generate Save & Resume Code
                </button>
              </div>
            ) : (
              <div style={{ background: '#f0fdf4', border: '2px solid #10b981', borderRadius: '12px', padding: '1.25rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#166534', textTransform: 'uppercase' }}>
                  Your Recovery Code:
                </div>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#15803d', letterSpacing: '0.1em', margin: '0.35rem 0' }}>
                  {savedResult.code}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#475569', marginBottom: '1rem' }}>
                  Saved at: {savedResult.savedAt} (Step {savedResult.step})
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="btn-primary"
                    style={{ flex: 1, padding: '0.6rem 0.85rem', fontSize: '0.85rem' }}
                  >
                    {copied ? '✅ Link Copied!' : '📋 Copy Resume Link'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary"
                    style={{ padding: '0.6rem 1rem', fontSize: '0.85rem' }}
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleResumeSubmit}>
            <p style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '1rem' }}>
              Enter your 6-character recovery code (e.g. <strong>MYS-8391</strong>) to restore your application:
            </p>

            <input
              type="text"
              placeholder="e.g. MYS-8391"
              value={resumeInputCode}
              onChange={(e) => setResumeInputCode(e.target.value.toUpperCase())}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                fontSize: '1.1rem',
                fontWeight: '800',
                letterSpacing: '0.05em',
                textAlign: 'center',
                borderRadius: '10px',
                border: '2px solid #2563eb',
                marginBottom: '0.75rem',
              }}
            />

            {errorMsg && (
              <div style={{ color: '#ef4444', fontSize: '0.825rem', fontWeight: '700', marginBottom: '0.75rem', textAlign: 'center' }}>
                {errorMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={onClose} className="btn-secondary" style={{ flex: 1 }}>
                Cancel
              </button>
              <button type="submit" disabled={isSearching} className="btn-primary" style={{ flex: 1 }}>
                {isSearching ? '⏳ Searching...' : '🔑 Restore Form'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
